import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';

import { NEUTRAL_TIER_COLOR, TIER_COLORS, SOURCE_LABELS } from '../constants';

import Avatar from '@/components/Avatar';
import Button from '@/components/Button';
import Card from '@/components/Card';
import Modal from '@/components/Modal';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import {
  PoolNominationRow,
  isClientReview,
} from '@/services/preferredPoolsAPI';
import styled from '@/styles';
import { formatISO } from '@/util/date';

// Per-source review modal (plan F7 / §5): a client nomination shows the exact
// rated shift; a system suggestion shows the qualifying criteria frozen at
// trigger time — the two shapes are intentionally different. Visual language
// from the prototype's #reviewModalOverlay (rv* ids kept as testids where the
// element ports); the prototype's dependability metrics (score ring, on-time
// rate, skill icons) come from GRAVY-10231 data that doesn't exist on this
// payload, so v1 renders the review facts the backend actually snapshots.

const Profile = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 12,
  textAlign: 'center',
  padding: '8px 0',
});

const TierBadge = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  borderRadius: 8,
  padding: '3px 8px',
  fontSize: '12px',
  fontWeight: 700,
  color: '#fff',
});

const FactRow = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 16,
  padding: '10px 0',
  borderBottom: '1px solid #EAEAEA',
  '&:last-child': { borderBottom: 'none' },
  fontSize: '14px',
});

const FactLabel = styled('span', { color: '#5B5B5B' });
const FactValue = styled('span', { fontWeight: 600, color: '#1C1C1C' });

const tierLabel = (tier: string | null): string =>
  tier ? tier.charAt(0).toUpperCase() + tier.slice(1) : '—';

const formatDate = (iso: string | null): string =>
  iso ? formatISO(iso, "EEE, MMM do yyyy 'at' h:mmbbb") : '—';

type Props = {
  nomination: PoolNominationRow;
  skillName: string;
  deciding: boolean;
  onApprove: (nomination: PoolNominationRow) => void;
  onDecline: (nomination: PoolNominationRow) => void;
  onClose: () => void;
};

const ReviewNominationModal = ({
  nomination,
  skillName,
  deciding,
  onApprove,
  onDecline,
  onClose,
}: Props) => {
  const { review, source } = nomination;

  return (
    <Modal
      disableClickout
      size="sm"
      title="Review nomination"
      onRequestClose={onClose}
    >
      <Card.Section>
        <Profile>
          <Avatar
            firstName={nomination.worker.name ?? ''}
            size="lg"
            src={nomination.worker.avatar_url ?? undefined}
          />
          <div data-testid="rvName" style={{ fontSize: 18, fontWeight: 700 }}>
            {nomination.worker.name}
          </div>
          <TierBadge
            data-testid="rvTierBadge"
            style={{
              background: nomination.tier
                ? TIER_COLORS[nomination.tier]
                : NEUTRAL_TIER_COLOR,
            }}
          >
            {tierLabel(nomination.tier)}
          </TierBadge>
          <Body>
            <b>{nomination.shifts_in_skill_total}</b> {skillName.toLowerCase()}{' '}
            shifts &middot; <b>{nomination.shifts_with_client}</b> shifts with
            client
          </Body>
          <Small>{SOURCE_LABELS[source] ?? source}</Small>
        </Profile>
      </Card.Section>
      <Card.Section>
        {isClientReview(review, source) ? (
          <div data-testid="reviewClientPayload">
            <FactRow>
              <FactLabel>Rating</FactLabel>
              <FactValue>
                {review.rating != null ? `${review.rating}/5` : '—'}
                {review.rated_by ? ` (rated by ${review.rated_by})` : ''}
              </FactValue>
            </FactRow>
            <FactRow>
              <FactLabel>Rated at</FactLabel>
              <FactValue>{formatDate(review.rated_at)}</FactValue>
            </FactRow>
            <FactRow>
              <FactLabel>Shift</FactLabel>
              <FactValue>{formatDate(review.shift_start_at)}</FactValue>
            </FactRow>
            <FactRow>
              <FactLabel>Location</FactLabel>
              <FactValue>{review.account_name ?? '—'}</FactValue>
            </FactRow>
            {review.job_id != null && (
              <FactRow>
                <FactLabel>Job</FactLabel>
                <FactValue>#{review.job_id}</FactValue>
              </FactRow>
            )}
          </div>
        ) : (
          <div data-testid="reviewSystemPayload">
            <FactRow>
              <FactLabel>Qualifying shifts</FactLabel>
              <FactValue>{review.qualifying_shift_count ?? '—'}</FactValue>
            </FactRow>
            <FactRow>
              <FactLabel>Qualifying score</FactLabel>
              <FactValue>{review.qualifying_score ?? '—'}</FactValue>
            </FactRow>
            <FactRow>
              <FactLabel>Skill</FactLabel>
              <FactValue>{review.skill_name ?? skillName}</FactValue>
            </FactRow>
            <FactRow>
              <FactLabel>Market</FactLabel>
              <FactValue>{review.market_name ?? '—'}</FactValue>
            </FactRow>
          </div>
        )}
        {nomination.also_qualified && (
          <div style={{ marginTop: 8 }}>
            <Small>
              Also qualified via{' '}
              {SOURCE_LABELS[String(nomination.also_qualified.source ?? '')] ??
                'another trigger'}
              .
            </Small>
          </div>
        )}
      </Card.Section>
      <Card.Section>
        <Stack justify="end">
          <Button
            a11yLabel="Reject nomination"
            appearance="outline"
            data-testid="rvRejectBtn"
            disabled={deciding}
            iconLeft={faTimes}
            label="Reject"
            status="danger"
            onClick={() => onDecline(nomination)}
          />
          <Button
            a11yLabel="Add to pool"
            appearance="outline"
            data-testid="rvAddBtn"
            disabled={deciding}
            iconLeft={faCheck}
            label="Add to pool"
            status="success"
            onClick={() => onApprove(nomination)}
          />
        </Stack>
      </Card.Section>
    </Modal>
  );
};

export default ReviewNominationModal;
